/* global document, window, MutationObserver */
import { css, Global } from '@emotion/core';
import {
  config as fontAwesomeConfig,
  dom as fontAwesomeDom,
} from '@fortawesome/fontawesome-svg-core';
import { ThemeProvider } from 'emotion-theming';
import throttle from 'lodash/throttle';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import emotionTheme from '../../../diTheme';
import '../../../legacyCore/_diDefaults.scss';
import WidgetScrollProvider from '../../../widgets/components/WidgetScrollProvider';
import WidgetUseProvider, { WidgetUseContext } from '../../../widgets/components/WidgetUseProvider';
import { updateWidgetContentHeight } from '../../../widgets/utils/widgetMessaging';
import { UniversalContext } from '../../../_universal';
import ErrorMessage from '../ErrorMessage';
import DIFavicon from './DIFavicon';
import Footer from './DiFooter';
import { FooterWrapper, HeaderWrapper, LayoutBody, LayoutContainer, LayoutWrapper } from './styles';

// Prevent fontawesome from dynamically adding its css, which breaks SSR
// See: https://github.com/FortAwesome/react-fontawesome/issues/134
fontAwesomeConfig.autoAddCss = false;

const DOM_AVAILABLE = typeof window !== 'undefined';

if (DOM_AVAILABLE) {
  // eslint-disable-next-line global-require
  require('mutationobserver-shim');
}

const DEFAULT_FOOTER_HEIGHT = 26;

class DiLayout extends Component {
  rootNode;
  footerNode;
  mutationObserver;

  static defaultProps = {
    size: 'lg',
  };

  static contextType = UniversalContext;

  state = { footerHeight: DEFAULT_FOOTER_HEIGHT, error: this.props.error };

  componentDidCatch(error) {
    this.onError(error);
    this.setState({ error });
  }

  componentDidMount() {
    const { error } = this.state;
    if (error) {
      this.onError(error);
    }
    if (!DOM_AVAILABLE) {
      return;
    }
    window.addEventListener('resize', this.onResize, false);
    window.addEventListener('message', this.onParentMessage, false);
    this.updateFooterHeight();
    this.updateIframeHeight();
  }

  componentWillUnmount() {
    if (!DOM_AVAILABLE) {
      return;
    }
    window.removeEventListener('resize', this.onResize, false);
    window.removeEventListener('message', this.onParentMessage, false);
  }

  onError(error) {
    // eslint-disable-next-line no-console
    console.error(error);
    this.context.trackError({
      error,
      properties: { component: 'WidgetLayout' },
    });
  }

  onResize = throttle(() => {
    this.updateFooterHeight();
    this.updateIframeHeight();
  }, 30);

  onMutation = throttle(() => {
    this.updateIframeHeight();
  }, 30);

  // To avoid resize loops we will force the scrollbar to be hidden
  // if the embed height matches the content height.
  onParentMessage(event) {
    let data = { matchesContentHeight: false };
    try {
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      }
    } catch (error) {
      return;
    }
    if (document.body) {
      document.body.style.overflowY = data.matchesContentHeight ? 'hidden' : '';
    }
  }

  setRootNode = (rootNode) => {
    this.rootNode = rootNode;
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (rootNode) {
      this.mutationObserver = new MutationObserver(this.onMutation);
      this.mutationObserver.observe(rootNode, {
        childList: true,
        subtree: true,
      });
    }
  };

  setFooterNode = (footerNode) => {
    this.footerNode = footerNode;
  };

  updateIframeHeight = () => {
    if (!DOM_AVAILABLE || window.parent == null) {
      return;
    }
    const rootHeight = this.rootNode ? this.rootNode.offsetHeight : 0;
    const currentFooterHeight = this.state.footerHeight;
    const nextFooterHeight = this.footerNode ? this.footerNode.offsetHeight : DEFAULT_FOOTER_HEIGHT;
    const contentHeight = rootHeight - currentFooterHeight + nextFooterHeight;
    updateWidgetContentHeight(contentHeight);
  };

  updateFooterHeight = () => {
    const footerHeight = this.footerNode ? this.footerNode.offsetHeight : DEFAULT_FOOTER_HEIGHT;
    this.setState(() => ({ footerHeight }));
  };

  render() {
    const { children, header, size, footerText, history, fullWidth, ...props } = this.props;
    const { error } = this.state;

    const bodyStyle = { paddingBottom: this.state.footerHeight };

    return (
      <WidgetUseProvider>
        <WidgetScrollProvider>
          <DIFavicon />
          <ThemeProvider theme={emotionTheme}>
            <Global
              styles={css`
                ${fontAwesomeDom.css()}
              `}
            />
            <WidgetUseContext.Consumer>
              {(widgetUseTracked) => (
                <LayoutWrapper
                  widgetUseTracked={widgetUseTracked}
                  {...props}
                  ref={this.setRootNode}
                >
                  {header ? (
                    <HeaderWrapper>
                      <LayoutContainer size={size} padding={{ _: 'sm', lg: 'md' }}>
                        {header}
                      </LayoutContainer>
                    </HeaderWrapper>
                  ) : (
                    <Footer text={footerText} marginY="lg" />
                  )}
                  <LayoutBody style={bodyStyle}>
                    <LayoutContainer size={size} fullWidth={fullWidth}>
                      {error == null ? children : <ErrorMessage errorDetails={error} />}
                    </LayoutContainer>
                  </LayoutBody>
                  {header && (
                    <FooterWrapper ref={this.setFooterNode}>
                      <Footer text={footerText} marginTop="md" />
                    </FooterWrapper>
                  )}
                </LayoutWrapper>
              )}
            </WidgetUseContext.Consumer>
          </ThemeProvider>
        </WidgetScrollProvider>
      </WidgetUseProvider>
    );
  }
}

export default withRouter(DiLayout);
